import {
  GetMediaDocument,
  LinkMediaDocument,
  type LinkMediaMutation,
  type LinkMediaMutationVariables,
  type MediaType,
  UploadMediaDocument,
  type UploadMediaMutation,
  type UploadMediaMutationVariables,
} from "@/generated/graphql";
import { useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { getMediaConfig, hasAudioText, validateFile } from "../config";
import { FileDropzone } from "../forms";
import { ModalShell } from "./ModalShell";
import { UploadPreviewPanel } from "./UploadPreviewPanel";

interface MediaUploadModalProps {
  /** Whether the modal is open */
  isOpen: boolean;
  /** Callback when modal should close */
  onClose: () => void;
  /** Media type to upload */
  mediaType: MediaType;
  /** Current folder ID (null for root) */
  folderId: string | null;
}

/**
 * File with metadata for upload
 */
export interface FileWithMetadata {
  file: File;
  audioText: string;
  preview: string; // Object URL for preview
  links: Partial<Record<MediaType, string[]>>; // Per-file links
  tags: string[]; // Per-file tags
}

/**
 * MediaUploadModal - Generic upload modal for all media types
 *
 * Uses configuration system to adapt behavior per media type.
 * Handles file upload, metadata, tags, and media linking.
 */
export function MediaUploadModal({ isOpen, onClose, mediaType, folderId }: MediaUploadModalProps) {
  const config = getMediaConfig(mediaType);

  // State
  const [files, setFiles] = useState<FileWithMetadata[]>([]);
  const [error, setError] = useState<string | null>(null);

  // Mutations
  const [uploadMedia, { loading }] = useMutation<UploadMediaMutation, UploadMediaMutationVariables>(
    UploadMediaDocument,
    {
      refetchQueries: [
        {
          query: GetMediaDocument,
          variables: { folderId: folderId || undefined, type: mediaType },
        },
      ],
    }
  );

  const [linkMedia] = useMutation<LinkMediaMutation, LinkMediaMutationVariables>(LinkMediaDocument);

  // Reset state when modal closes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Only run cleanup when modal closes, not when files change
  useEffect(() => {
    if (!isOpen) {
      // Revoke object URLs to prevent memory leaks
      for (const f of files) {
        URL.revokeObjectURL(f.preview);
      }
      setFiles([]);
      setError(null);
    }
  }, [isOpen]);

  // Handle file selection
  const handleFilesChange = (newFiles: File[]) => {
    const validFiles: FileWithMetadata[] = [];

    for (const file of newFiles) {
      // Validate file
      const validation = validateFile(file, mediaType);
      if (!validation.valid) {
        setError(validation.error || "Invalid file");
        continue;
      }

      validFiles.push({
        file,
        audioText: "",
        preview: URL.createObjectURL(file),
        links: {},
        tags: [],
      });
    }

    setFiles((prev) => [...prev, ...validFiles]);
  };

  // Handle file removal
  const handleRemoveFile = (index: number) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      const removed = newFiles.splice(index, 1)[0];
      if (removed) {
        URL.revokeObjectURL(removed.preview);
      }
      return newFiles;
    });
  };

  // Handle metadata update for a specific file
  const handleUpdateMetadata = (index: number, updates: Partial<FileWithMetadata>) => {
    setFiles((prev) => {
      const newFiles = [...prev];
      const file = newFiles[index];
      if (file) {
        newFiles[index] = { ...file, ...updates };
      }
      return newFiles;
    });
  };

  // Handle save
  const handleSave = async () => {
    if (files.length === 0) {
      setError("Please select at least one file");
      return;
    }

    // Check that each file has at least one tag
    const filesWithoutTags = files.filter((f) => f.tags.length === 0);
    if (filesWithoutTags.length > 0) {
      setError(
        `Please add at least one tag to each file (${filesWithoutTags.length} file${filesWithoutTags.length > 1 ? "s" : ""} missing tags)`
      );
      return;
    }

    setError(null);

    try {
      for (const fileData of files) {
        // Upload file with per-file tags
        const result = await uploadMedia({
          variables: {
            file: fileData.file,
            input: {
              folderId,
              type: mediaType,
              tags: fileData.tags,
              ...(hasAudioText(mediaType) && { audioText: fileData.audioText }),
            },
          },
        });

        const uploadedMediaId = result.data?.uploadMedia.id;

        // Create per-file links if any
        if (uploadedMediaId) {
          for (const [, linkIds] of Object.entries(fileData.links)) {
            for (const linkId of linkIds || []) {
              try {
                await linkMedia({
                  variables: {
                    input: {
                      fromId: uploadedMediaId,
                      toId: linkId,
                    },
                  },
                });
              } catch (linkError) {
                // Continue even if linking fails (might be duplicate)
                console.warn("Link creation failed:", linkError);
              }
            }
          }
        }
      }

      toast.success(
        `${files.length} ${files.length === 1 ? config.label.toLowerCase() : config.labelPlural.toLowerCase()} uploaded`
      );
      onClose();
    } catch (err) {
      const message = err instanceof Error ? err.message : "Upload failed";
      setError(message);
      toast.error(message);
    }
  };

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={onClose}
      title={config.uploadTitle}
      singleColumn
      leftPanel={
        <div className="space-y-6">
          {/* Compact dropzone */}
          {files.length === 0 ? (
            <FileDropzone
              files={[]}
              onFilesChange={handleFilesChange}
              accept={config.acceptedMimeTypes}
              maxSize={config.maxFileSize}
              label={`${config.label} files`}
              required
              validateFile={(file) => validateFile(file, mediaType)}
            />
          ) : (
            <div className="flex items-center justify-between rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-3">
              <span className="text-sm text-gray-600">
                {files.length} {files.length === 1 ? "file" : "files"} selected
              </span>
              <label className="cursor-pointer rounded-md bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
                Add more
                <input
                  type="file"
                  multiple
                  accept={config.acceptedMimeTypes}
                  onChange={(e) => {
                    if (e.target.files) {
                      handleFilesChange(Array.from(e.target.files));
                    }
                    e.target.value = "";
                  }}
                  className="sr-only"
                />
              </label>
            </div>
          )}

          {/* File cards */}
          {files.length > 0 && (
            <UploadPreviewPanel
              files={files}
              mediaType={mediaType}
              onRemoveFile={handleRemoveFile}
              onUpdateMetadata={handleUpdateMetadata}
            />
          )}
        </div>
      }
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={loading || files.length === 0}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
          >
            {loading ? "Uploading..." : "Save"}
          </button>
        </>
      }
      error={error}
    />
  );
}

export default MediaUploadModal;
